'use client'

import { sendGTMEvent } from '@next/third-parties/google'
import { Dispatch, SetStateAction, useCallback } from 'react'

import { BI_CATEGORY_SIGN_UP, BI_EVENT_WORLD_SIGN_UP } from '@/constants/bi-sdk'
import { useBISdkContext } from '@/context/BISdkContext'
import { useConfigsContext } from '@/context/ConfigsContext'
import { useUAContext } from '@/context/UAContext'
import useGetCommunityToggle from '@/hooks/auth/useGetCommunityToggle'
import { createProfile, createViveportAuth, extractUsernameFromEmail } from '@/lib/auth'
import { enableLoginStatusCheck } from '@/lib/configs'
import { getViveCreatorUrl } from '@/lib/url'
import { profileAvatarSchema, profileIdSchema, profileUserIdSchema } from '@/schemas/auth'
import { getProfileMe } from '@/services/auth'
import { HubsStore } from '@/types/store'

const useHandleProfileResult = (setIsSignedIn: Dispatch<SetStateAction<boolean>>) => {
  const getCommunityToggle = useGetCommunityToggle()
  const { sendBIReport } = useBISdkContext()
  const { setStore } = useConfigsContext()
  const { isMobileApp } = useUAContext()

  return useCallback(
    async (credentials: HubsStore['credentials'], qsHtcToken: string | null) => {
      const htcToken = credentials?.htcToken
      const email = credentials?.email
      const result = await getProfileMe(htcToken!)

      if (!result) {
        throw new Error('Error while fetching profile')
      }

      const { id, displayName, activeAvatar, userId } = result
      const htcAccountId = extractUsernameFromEmail(email)

      const isIdNotSet = !profileIdSchema.safeParse({ id }).success
      const isUserIdNotSet = !profileUserIdSchema.safeParse({ userId }).success
      const isNewUser = !profileAvatarSchema.safeParse({ displayName, activeAvatar }).success

      const communityToggle = await getCommunityToggle(credentials)
      const isProfileNotSet = isNewUser || (communityToggle && isUserIdNotSet)

      if (isNewUser) {
        sendBIReport({
          category: BI_CATEGORY_SIGN_UP,
          ev: BI_EVENT_WORLD_SIGN_UP,
          account_id: htcAccountId,
        })
      }

      if (isIdNotSet) {
        // TODO: handle error
        throw new Error(`Couldn't get user id`, { cause: 'connect-failed' })
      } else if (isProfileNotSet) {
        const viveCreatorUrl = getViveCreatorUrl()
        // force redirect to vive creator page
        window.location.href = viveCreatorUrl
      } else {
        const profile = createProfile(result)
        const viveportAuth = createViveportAuth(result)

        setStore((value) => ({
          ...value,
          viveportAuth: {
            ...value.viveportAuth,
            ...viveportAuth,
          },
          profile: {
            ...value.profile,
            ...profile,
          },
          communityToggle,
          credentials,
        }))

        setIsSignedIn(true)

        if (isMobileApp && 'flutterWebview' in window) {
          // TODO: define flutterWebview type
          // @ts-ignore
          window.flutterWebview.postMessage(
            JSON.stringify({
              feature: 'profile',
              data: {
                headIconUrl: profile.avatarSnapshot,
                displayName: profile.displayName,
              },
            }),
          )
        }
      }

      if (enableLoginStatusCheck() && qsHtcToken) {
        if (!isNewUser) {
          sendGTMEvent({ event: 'login', account_id: htcAccountId })
        } else {
          sendGTMEvent({ event: 'signup', account_id: htcAccountId })
        }
      }
    },
    [getCommunityToggle, sendBIReport, setIsSignedIn, setStore, isMobileApp],
  )
}

export default useHandleProfileResult
